import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, type ReactNode, useState } from 'react';
import { Alert, Badge, Button, Card, Checkbox, Field, Select, Spinner } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/errors';
import { type CatalogField, type CatalogInfo, type CatalogItem, KEYS } from './data';

type Editing = { kind: 'ninguno' } | { kind: 'nuevo' } | { kind: 'item'; id: string };

/**
 * Mantenedor genérico de un catálogo (estructura común del Panel): la tabla y el formulario salen de los campos
 * que define la API. Agregar un catálogo no exige código en la interfaz.
 */
export function CatalogSection({ catalog }: { catalog: CatalogInfo }) {
  const qc = useQueryClient();
  const key = KEYS.items(catalog.key);
  const base = `/api/parametros/catalogos/${catalog.key}`;
  // Siempre con los inactivos: el orden se fija sobre la lista completa.
  const items = useQuery({ queryKey: key, queryFn: () => api<CatalogItem[]>(`${base}?inactivos=true`) });
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<Editing>({ kind: 'ninguno' });
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function act(fn: () => Promise<unknown>, message?: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: key });
      if (message) setNotice(message);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const all = items.data ?? [];
  const visible = showInactive ? all : all.filter((i) => i.active);

  /** Mueve un registro una posición (entre los visibles) y guarda el orden completo. */
  function move(item: CatalogItem, delta: -1 | 1) {
    const i = visible.indexOf(item);
    const other = visible[i + delta];
    if (!other) return;
    const ids = all.map((x) => x.id);
    const a = ids.indexOf(item.id);
    const b = ids.indexOf(other.id);
    [ids[a], ids[b]] = [ids[b]!, ids[a]!];
    void act(() => api(`${base}/orden`, { json: { ids } }));
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{catalog.title}</h2>
          <p className="max-w-2xl text-sm text-slate-600">{catalog.description}</p>
        </div>
        <Button
          onClick={() => {
            setNotice(null);
            setEditing({ kind: 'nuevo' });
          }}
        >
          {t('cat.nuevo')}
        </Button>
      </div>
      {error && <Alert>{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}

      {editing.kind === 'nuevo' && (
        <Card title={`${t('cat.nuevo')}: ${catalog.title}`}>
          <ItemForm
            fields={catalog.fields}
            busy={busy}
            onCancel={() => setEditing({ kind: 'ninguno' })}
            onSubmit={(values) =>
              act(async () => {
                await api(base, { json: values });
                setEditing({ kind: 'ninguno' });
              }, t('cat.creado'))
            }
          />
        </Card>
      )}

      {!items.data ? (
        items.error ? <Alert>{errorMessage(items.error)}</Alert> : <Spinner label={t('comun.cargando')} />
      ) : (
        <Card>
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="text-xs text-slate-500">{t('cat.no_se_borran')}</p>
            <Checkbox label={t('cat.mostrar_inactivos')} checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          </div>
          {visible.length === 0 ? (
            <p className="text-sm text-slate-500">{t('cat.vacio')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-slate-200 text-xs text-slate-500">
                  <tr>
                    {catalog.fields.map((f) => (
                      <th key={f.name} className="px-2 py-2 font-medium">
                        {f.label}
                      </th>
                    ))}
                    <th className="px-2 py-2 font-medium">{t('cat.estado')}</th>
                    <th className="px-2 py-2 text-right font-medium">{t('cat.acciones')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {visible.map((item, index) =>
                    editing.kind === 'item' && editing.id === item.id ? (
                      <tr key={item.id}>
                        <td colSpan={catalog.fields.length + 2} className="px-2 py-3">
                          <ItemForm
                            fields={catalog.fields}
                            initial={item}
                            busy={busy}
                            onCancel={() => setEditing({ kind: 'ninguno' })}
                            onSubmit={(values) =>
                              act(async () => {
                                await api(`${base}/${item.id}`, { method: 'PATCH', json: values });
                                setEditing({ kind: 'ninguno' });
                              }, t('cat.guardado'))
                            }
                          />
                        </td>
                      </tr>
                    ) : (
                      <tr key={item.id} className={item.active ? '' : 'text-slate-400'}>
                        {catalog.fields.map((f) => (
                          <td key={f.name} className="px-2 py-2">
                            {formatValue(f, item[f.name])}
                          </td>
                        ))}
                        <td className="px-2 py-2">{!item.active && <Badge tone="amber">{t('cat.inactivo')}</Badge>}</td>
                        <td className="whitespace-nowrap px-2 py-2 text-right">
                          <span className="inline-flex gap-1">
                            <IconButton label={`${t('cat.subir')} ${label(catalog, item)}`} disabled={busy || index === 0} onClick={() => move(item, -1)}>
                              ↑
                            </IconButton>
                            <IconButton label={`${t('cat.bajar')} ${label(catalog, item)}`} disabled={busy || index === visible.length - 1} onClick={() => move(item, 1)}>
                              ↓
                            </IconButton>
                            <Button variant="secondary" className="px-3 py-1" aria-label={`${t('cat.editar')} ${label(catalog, item)}`} onClick={() => setEditing({ kind: 'item', id: item.id })}>
                              {t('cat.editar')}
                            </Button>
                            <Button
                              variant="secondary"
                              className="px-3 py-1"
                              disabled={busy}
                              aria-label={`${item.active ? t('cat.desactivar') : t('cat.activar')} ${label(catalog, item)}`}
                              onClick={() => void act(() => api(`${base}/${item.id}`, { method: 'PATCH', json: { active: !item.active } }), t('cat.guardado'))}
                            >
                              {item.active ? t('cat.desactivar') : t('cat.activar')}
                            </Button>
                          </span>
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

/** Nombre del registro para las etiquetas accesibles: el primer campo. */
const label = (catalog: CatalogInfo, item: CatalogItem) => String(item[catalog.fields[0]!.name] ?? '');

function formatValue(f: CatalogField, value: unknown): ReactNode {
  if (f.type === 'boolean') return value ? t('param.si') : t('param.no');
  if (value === null || value === undefined || value === '') return f.type === 'select' || f.type === 'reference' ? (f.nullLabel ?? '—') : '—';
  if (f.type === 'select' || f.type === 'reference') return f.options.find((o) => o.value === value)?.label ?? String(value);
  if (f.type === 'decimal') return Number(value).toLocaleString('es-PA', { maximumFractionDigits: 2 });
  if (f.type === 'color') {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block h-3.5 w-3.5 rounded border border-slate-300" style={{ backgroundColor: String(value) }} aria-hidden />
        <span className="font-mono text-xs">{String(value)}</span>
      </span>
    );
  }
  return String(value);
}

function IconButton({ label: ariaLabel, children, ...props }: { label: string; children: string; disabled?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      title={ariaLabel}
      className="rounded border border-slate-300 px-2 py-1 text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
      {...props}
    >
      {children}
    </button>
  );
}

function ItemForm({
  fields,
  initial,
  busy,
  onSubmit,
  onCancel,
}: {
  fields: CatalogField[];
  initial?: CatalogItem;
  busy: boolean;
  onSubmit: (values: Record<string, unknown>) => Promise<void>;
  onCancel: () => void;
}) {
  const [values, setValues] = useState<Record<string, unknown>>(() =>
    Object.fromEntries(fields.map((f) => [f.name, initial?.[f.name] ?? (f.type === 'boolean' ? false : f.type === 'color' ? '#64748B' : '')])),
  );
  const set = (name: string, value: unknown) => setValues((v) => ({ ...v, [name]: value }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    // Números como número; vacío como null en los opcionales (la API hace lo mismo). Al editar no se envían los
    // campos inmutables: la API los rechaza.
    const out = Object.fromEntries(
      fields
        .filter((f) => !(initial && f.immutable))
        .map((f) => {
          const v = values[f.name];
          const empty = v === '' || v === null || v === undefined;
          if (f.type === 'integer' || f.type === 'decimal') return [f.name, empty ? null : Number(v)];
          if (f.type === 'text') return [f.name, typeof v === 'string' ? v.trim() || (f.required ? '' : null) : v];
          if (f.type === 'select' || f.type === 'reference') return [f.name, empty ? null : v];
          return [f.name, v];
        }),
    );
    void onSubmit(out);
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        {fields.map((f) => {
          const id = `cat-${f.name}-${initial?.id ?? 'nuevo'}`;
          const locked = !!initial && !!f.immutable;
          let input: ReactNode;
          switch (f.type) {
            case 'boolean':
              input = <Checkbox id={id} label={f.label} checked={values[f.name] === true} disabled={locked} onChange={(e) => set(f.name, e.target.checked)} />;
              break;
            case 'select':
            case 'reference':
              input = (
                <Select id={id} label={f.label} required={f.required} disabled={locked} value={String(values[f.name] ?? '')} onChange={(e) => set(f.name, e.target.value)}>
                  <option value="" disabled={f.required}>
                    {f.required ? '—' : (f.nullLabel ?? '—')}
                  </option>
                  {f.options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              );
              break;
            case 'color':
              input = (
                <Field
                  id={id}
                  label={f.label}
                  type="color"
                  required={f.required}
                  disabled={locked}
                  className="h-10 w-20 cursor-pointer rounded-md border border-slate-300 p-1"
                  value={String(values[f.name] ?? '#64748B')}
                  onChange={(e) => set(f.name, e.target.value.toUpperCase())}
                />
              );
              break;
            default:
              input = (
                <Field
                  id={id}
                  label={f.label}
                  type={f.type === 'integer' || f.type === 'decimal' ? 'number' : f.type === 'text' && f.format === 'url' ? 'url' : 'text'}
                  step={f.type === 'decimal' ? 'any' : f.type === 'integer' ? '1' : undefined}
                  required={f.required}
                  disabled={locked}
                  maxLength={f.type === 'text' ? f.maxLength : undefined}
                  min={f.type === 'integer' || f.type === 'decimal' ? f.min : undefined}
                  max={f.type === 'integer' || f.type === 'decimal' ? f.max : undefined}
                  value={String(values[f.name] ?? '')}
                  onChange={(e) => set(f.name, e.target.value)}
                />
              );
          }
          return (
            <div key={f.name} className="space-y-1">
              {input}
              {f.help && <p className="text-xs text-slate-500">{f.help}</p>}
            </div>
          );
        })}
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={busy}>
          {t('comun.guardar')}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          {t('comun.cancelar')}
        </Button>
      </div>
    </form>
  );
}
